// One photo through the export worker with everything that travels with it: the background matte
// (§11.12), the LUT (§11.6) and the grain seed (§11.19). Used by the Export dialog, the contact sheet and
// the Simple-mode Save buttons. With upscale on (M13) the upscaled PNG is the source.
import { exportPhoto } from '../io/exportClient';
import { matteFor } from './background/matte';
import { objectMattesFor } from './background/objectMask';
import { useClipboard } from '../state/clipboard';
import { getLut } from './lut/store';
import { grainSeed } from '../engine/grain';
import { upscaledBlob } from './upscale/upscale';
import type { ExportOptions } from '../engine/exportRenderer';
import type { MetadataSettings } from '../io/encode/xmp';
import type { LibraryPhoto } from '../state/library';
import type { Settings } from '../model/types';

export async function exportWithExtras(
  photo: LibraryPhoto | undefined,
  settings: Settings,
  opts: ExportOptions,
  meta: MetadataSettings,
  file?: Blob,
) {
  // M13: an upscaled photo exports from its upscaled source (the original file is never changed).
  file ??= photo ? ((await upscaledBlob(photo, settings)) ?? photo.file) : undefined;
  if (!file) throw new Error('exportWithExtras: no photo and no file');
  const matte = photo && settings.background.mode !== 'off' ? await matteFor(photo, settings) : null;
  const lut = settings.lut.id ? await getLut(settings.lut.id) : null;
  const seed = photo ? grainSeed(photo.name, photo.size, photo.lastModified) : 1;
  // M22: the object-pick mattes of the photo's object masks (cached; computed if missing).
  const objects =
    photo && settings.masks.some((m) => m.kind === 'object') ? await objectMattesFor(photo, settings) : [];
  // Your call Y2: a mask whose matte could not be made (model not on this PC, or it failed) exports without it,
  // and the user is told.
  if (missingObjectMasks(settings, objects.length))
    useClipboard.getState().setToast({ key: 'exportObjectMaskMissing', ms: 8000 });
  return exportPhoto(file, settings, { ...opts, grainSeed: seed }, meta, matte?.matte, lut, objects);
}

/** Object masks the settings call for (with at least one tap) that have no matte to export with. */
export function missingObjectMasks(settings: Settings, mattes: number): number {
  const wanted = settings.masks.filter((m) => m.kind === 'object' && 'points' in m.geom && m.geom.points.length > 0).length;
  return Math.max(0, wanted - mattes);
}
